import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateFeatureTag,
  getAdminGetFeatureTagsQueryKey,
  useAdminGetProducts,
} from '@/api/auth/admin/admin';
import type { FeatureTagResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface EditFeatureTagDialogProps {
  /** The feature tag being edited, or null when the dialog is closed. */
  featureTag: FeatureTagResponse | null;
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback when the dialog open state changes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for editing a feature catalog tag's product and description. The feature code is
 * immutable and shown read-only.
 */
export function EditFeatureTagDialog({
  featureTag,
  open,
  onOpenChange,
}: EditFeatureTagDialogProps) {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [description, setDescription] = useState('');

  const { data: productsData } = useAdminGetProducts();
  const products = productsData?.data?.products || [];

  // Seed the form whenever a different tag is opened for editing.
  useEffect(() => {
    if (featureTag) {
      setProductId(featureTag.productId);
      setDescription(featureTag.description);
    }
  }, [featureTag]);

  const { mutate: updateFeatureTag, isPending: isSaving } = useAdminUpdateFeatureTag({
    mutation: {
      onSuccess: () => {
        toast.success('Feature tag updated');
        queryClient.invalidateQueries({
          queryKey: getAdminGetFeatureTagsQueryKey(),
        });
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateFeatureTag', featureTagId: featureTag?.id });
        toast.error(
          'Failed to update feature tag. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const handleSave = () => {
    if (!featureTag) {
      return;
    }
    updateFeatureTag({
      featureTagId: featureTag.id,
      data: {
        productId,
        description: description.trim(),
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Feature Tag</DialogTitle>
          <DialogDescription>
            Update the product or description. The feature code cannot be changed.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Feature Code</Label>
            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
              {featureTag?.featureCode}
            </code>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="editProductId">Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger id="editProductId">
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.productId} value={product.productId}>
                    {product.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="editDescription">Description</Label>
            <Input
              id="editDescription"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !productId || !description.trim()}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
